import { toast } from 'react-semantic-toasts';
import PropTypes from 'prop-types';
import { getNotificationIcon, isAllLocations } from '../helpers/utils';
import { history } from '../store/configureStore';
import { verifyAccount } from './auth';
import userService from '../services/userServices';
import { updateNotificationCount } from './notifications';

export const getWebsocketTokenRequest = () => ({ type: 'WEBSOCKET_TOKEN_REQUEST' });
export const getWebsocketTokenSuccess = (url, token) => ({ type: 'WEBSOCKET_TOKEN_SUCCESS', url, token });
export const getWebsocketTokenApiError = (message, form) => ({ type: 'WEBSOCKET_TOKEN_API_ERROR', message, form });

export const onWebsocketConnected = (event) => ({ type: 'WEBSOCKET_CONNECTED', event });
export const onWebsocketDisconnected = (event) => ({ type: 'WEBSOCKET_DISCONNECTED', event });
export const onWebsocketMessage = (event) => ({ type: 'WEBSOCKET_MESSAGE', event });
export const onWebsocketEvent = (event) => ({ type: 'WEBSOCKET_EVENT', event });
export const onWebsocketError = (event) => ({ type: 'WEBSOCKET_ERROR', event });

export const clearWebsocketEvent = () => ({ type: 'WEBSOCKET_CLEAR_EVENT' });

export const imageRefresh = () => ({ type: 'IMAGE_VERSION_UPDATE' });
const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

export const notificationCountRefresh = (msg) => ({ type: 'NOTIFICATION_COUNT_REFRESH', msg });

const generateUrl = (protocol, address, token) => {
    return `${protocol}//${address}?tokenId=${token}`;
};

export const getWebsocketUrl = () => (dispatch) =>
    new Promise((resolve) => {
        dispatch(getWebsocketTokenRequest());

        userService.getWebsocketToken().then(
            (response) => {
                const { wsUrl, tokenId } = response.data;
                const websocketUrl = generateUrl(proto, wsUrl, tokenId);
                dispatch(getWebsocketTokenSuccess(wsUrl, tokenId));
                resolve(websocketUrl);
            },
            (error) => {
                const { message, form = {} } = error.data;
                dispatch(getWebsocketTokenApiError(message, form));
            }
        );
    });

const getLevel = (level = '') => {
    switch (level) {
        case 'INFO':
            return 'info';

        case 'SUCCESS':
            return 'success';

        case 'WARNING':
            return 'warning';

        case 'ERROR':
            return 'error';

        default:
            return 'info';
    }
};

const sendToast = ({
    type = 'success',
    time = 10000,
    title = undefined,
    message,
    onClick = undefined,
    onClose = undefined,
    onDismiss = undefined
}) => {
    const options = {
        type,
        icon: getNotificationIcon(type),
        title,
        animation: 'bounce',
        time,
        onClose,
        onClick,
        onDismiss
    };

    if (Array.isArray(message)) {
        if (message.length === 1) {
            const [description] = message;
            options.description = description;
        } else if (message.length > 1) {
            options.list = message;
        }
    } else {
        options.description = message;
    }

    toast(options);
};

const sendNotification = (dispatch, msg) => {
    if (msg) {
        const { notificationType } = msg;
        const level = getLevel(msg.level);
        switch (notificationType) {
            case 'success':
            case 'info':
            case 'warning':
            case 'error':
                dispatch(onWebsocketMessage(msg));

                sendToast({
                    type: notificationType,
                    title: msg.header,
                    message: msg.message,
                    onClick: msg.onClick,
                    onClose: msg.onClose,
                    onDismiss: msg.onDismiss
                });
                break;

            case 'PROVISION_RESPONSE':
                // send event to make sure reload data occurs
                dispatch(onWebsocketEvent(msg));

                sendToast({
                    type: level,
                    message: msg.message,
                    onClick: () => history.push('/sims')
                });
                break;

            case 'BACKUP_RESPONSE':
                dispatch(onWebsocketEvent(msg));

                sendToast({
                    type: level,
                    message: msg.message,
                    onClick: () => history.push('/sims/backup')
                });
                break;

            case 'RESTORE_RESPONSE':
                dispatch(onWebsocketEvent(msg));

                sendToast({
                    type: level,
                    message: msg.message,
                    onClick: () => history.push('/sims/restore')
                });
                break;

            case 'AUDIT_RESPONSE':
                dispatch(onWebsocketEvent(msg));

                sendToast({
                    type: level,
                    message: msg.message,
                    onClick: () => history.push('/sims/audit')
                });
                break;

            case 'ACTION_UPDATE':
                if (level === 'error') {
                    dispatch(onWebsocketEvent(msg));
                } else {
                    dispatch(onWebsocketMessage(msg));
                }

                sendToast({
                    type: level,
                    title: msg.header,
                    message: msg.message
                });
                break;

            case 'UNITYSERVER_RESPONSE':
                updateRenewViewImage(dispatch);
                break;
            case 'SOFTWARE_UPGRADE':
                const uncheckedNotifications = msg?.payload?.uncheckedNotifications;
                const uncheckedFutureNotifications = msg?.payload?.uncheckedFutureNotifications;
                updateNotificationCount(dispatch, uncheckedNotifications + uncheckedFutureNotifications);
                break;
            default:
        }
    }
};

/**
 *
 *
 * @Deprecated use hooks with sendNotification
 * @param msg
 * @returns {function(*=): void}

 */
export const sendNotificationOld = (msg) => (dispatch) => sendNotification(dispatch, msg);

const processDataSync = (dispatch, msg) => {
    dispatch(onWebsocketEvent(msg));
};

const processSessionRefresh = (dispatch) => {
    dispatch(verifyAccount('verify'));
};

const updateRenewViewImage = (dispatch) => {
    console.log('inside updateRenewViewImage');
    dispatch(imageRefresh());
};
export const processMessage = (event) => (dispatch, getState) => {
    try {
        if (event) {
            const { admin, enterpriseId, linkedEnterprises } = getState().auth;
            const linkedEnterpriseIds = linkedEnterprises?.map((linkedEnterprise) => linkedEnterprise.enterpriseId) || [];
            const { data } = event;
            const message = JSON.parse(data);

            if (message) {
                const { notificationType, enterpriseId: enterpriseIdMsg } = message;

                if (typeof message.payload === 'string') {
                    try {
                        message.payload = JSON.parse(message.payload);
                    } catch (e) {}
                }

                if (notificationType === 'SESSION') {
                    processSessionRefresh(dispatch);
                } else if (notificationType === 'SOFTWARE_UPGRADE') {
                    sendNotification(dispatch, message);
                } else if (
                    !admin ||
                    (enterpriseIdMsg !== undefined && enterpriseIdMsg === enterpriseId) ||
                    // if all locations are selected, show notification for all linked locations
                    (isAllLocations() && enterpriseIdMsg !== undefined && linkedEnterpriseIds?.includes(enterpriseIdMsg))
                ) {
                    switch (notificationType) {
                        case 'DATA_SYNC':
                            processDataSync(dispatch, message);
                            break;

                        default:
                            sendNotification(dispatch, message);
                            break;
                    }
                }
            }
        }
    } catch (e) {}
};

processMessage.prototype = {
    notificationType: PropTypes.string,
    level: PropTypes.string,
    header: PropTypes.string,
    message: PropTypes.string,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    onDismiss: PropTypes.func
};
